import { createDirectus, rest, readItems } from '@directus/sdk';

const getDirectusUrl = (): string => {
  const envUrl = import.meta.env.VITE_DIRECTUS_API_URL;
  
  // Если переменная не задана или пустая строка, используем fallback
  if (!envUrl || envUrl.trim() === '') {
    return 'https://cms.ircomp.ru';
  }
  
  let url = envUrl.trim().replace(/\/$/, '');
  
  // Если URL начинается с /, добавляем origin только в браузере
  if (url.startsWith('/')) {
    if (typeof window !== 'undefined') {
      url = window.location.origin + url;
    } else {
      // В SSR окружении не можем определить origin, используем fallback
      return 'https://cms.ircomp.ru';
    }
  }
  
  // Проверяем, что URL валиден (имеет протокол)
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    // Если нет протокола, добавляем https://
    url = 'https://' + url;
  }
  
  return url;
};

const DIRECTUS_URL = getDirectusUrl();

// Сайт o4ki.online имеет ID=3 в коллекции sites Directus
const SITE_ID = 3;

// Базовый экземпляр Directus с кастомным fetch (для публичных данных с доступом к сайту)
// Используем кастомный fetch через глобальную настройку
export const directus = createDirectus(DIRECTUS_URL).with(rest({
  onRequest: (options) => {
    const headers: Record<string, string> = {};
    // Копируем существующие заголовки, если они есть
    if (options.headers) {
      if (typeof options.headers === 'object' && !Array.isArray(options.headers)) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (value !== undefined) {
            headers[key] = String(value);
          }
        });
      }
    }
    const siteAccessKey = import.meta.env.VITE_SITE_ACCESS_KEY;
    if (siteAccessKey) {
      headers['X-Site-Access-Key'] = siteAccessKey;
    }
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return { ...options, headers };
  },
}));

// Экземпляр Directus с ключом доступа к сайту (для обратной совместимости)
export const directusWithSiteKey = directus;

// Экземпляр Directus без заголовка X-Site-Access-Key для поиска сайтов
// (публичный доступ к коллекции sites)
export const directusPublic = createDirectus(DIRECTUS_URL).with(rest({
  onRequest: (options) => {
    const headers: Record<string, string> = {};
    // Копируем существующие заголовки, если они есть
    if (options.headers) {
      if (typeof options.headers === 'object' && !Array.isArray(options.headers)) {
        Object.entries(options.headers).forEach(([key, value]) => {
          if (value !== undefined) {
            headers[key] = String(value);
          }
        });
      }
    }
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return { ...options, headers };
  },
}));

// Типы
export interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover: string | null;
  published_at: string;  // Оставляем для обратной совместимости
  publish_date: string;  // Добавляем новое поле для отображения даты публикации
  tags: string[];
  categories: Array<{
    categories_id: {
      id: number;
      name: string;
      slug: string;
      parent: number | null;
    };
  }>;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  parent: number | null;
}

// Отладочная функция для получения всех сайтов
export const getAllSites = async () => {
  try {
    const sites = await directusWithSiteKey.request(
      readItems('sites', {
        fields: ['id', 'domain', 'name', 'satus']
      })
    );
    console.log('Все сайты в Directus:', sites);
    return Array.isArray(sites) ? sites : [];
  } catch (error) {
    console.error('Ошибка при получении сайтов:', error);
    return [];
  }
};

// Функция для получения постов по домену сайта
export const getPostsForSite = async (domain: string): Promise<Post[]> => {
  try {
    console.log(`Получение постов для домена: ${domain}`);
    
    // Временное решение: используем фиксированный ID сайта для o4ki.online
    // Сайт o4ki.online имеет ID=3 в Directus
    const siteId = 3;
    console.log(`Используем фиксированный ID сайта: ${siteId} для домена ${domain}`);
    
    // Используем прямой fetch к Directus API (как в тестовой странице)
    const baseUrl = import.meta.env.VITE_DIRECTUS_API_URL;
    const url = `${baseUrl}/items/posts?` +
      `filter[status][_eq]=published&` +
      `filter[sites][sites_id][_eq]=${siteId}&` +
      `sort=-publish_date&` +
      `fields=id,title,slug,excerpt,content,cover,published_at,publish_date,tags,categories.categories_id.id,categories.categories_id.name,categories.categories_id.slug,categories.categories_id.parent`;
    
    console.log(`Запрос к Directus: ${url}`);
    
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    const posts = result.data || [];
    
    console.log(`Получено ${posts.length} постов`);
    
    // Для отладки: выведем первые несколько постов
    if (posts.length > 0) {
      console.log('Пример поста:', {
        id: posts[0].id,
        title: posts[0].title,
        slug: posts[0].slug,
        published_at: posts[0].published_at,
        publish_date: posts[0].publish_date
      });
    }
    
    return posts as Post[];
  } catch (error) {
    console.error('Ошибка в getPostsForSite:', error);
    // Для отладки выведем полную информацию об ошибке
    if (error instanceof Error) {
      console.error('Сообщение об ошибке:', error.message);
      console.error('Стек вызовов:', error.stack);
    }
    return [];
  }
};

// Функция для получения последних опубликованных новостей (без полного текста поста).
// Используется на главной странице, чтобы не скачивать содержимое всех постов.
// ВАЖНО: запрос идёт через directusPublic. Экземпляр `directus` добавляет заголовок
// X-Site-Access-Key, а Directus отвечает на preflight как
// `Access-Control-Allow-Headers: Content-Type,Authorization`, поэтому браузер
// блокирует такой запрос и блок новостей остаётся пустым.
// Ошибку не глотаем — иначе её не отличить от «новостей действительно нет».
export const getLatestPosts = async (limit: number): Promise<Post[]> => {
  const posts = await directusPublic.request(
    readItems('posts', {
      limit,
      sort: ['-publish_date'],
      filter: {
        status: { _eq: 'published' },
        sites: { sites_id: { _eq: SITE_ID } },
      },
      fields: [
        'id',
        'title',
        'slug',
        'excerpt',
        'cover',
        'published_at',
        'publish_date',
        'tags',
        'categories.categories_id.id',
        'categories.categories_id.name',
        'categories.categories_id.slug',
        'categories.categories_id.parent',
      ],
    })
  );

  return Array.isArray(posts) ? (posts as unknown as Post[]) : [];
};

// Функция для получения конкретного поста по slug
export const getPostBySlug = async (slug: string, domain: string): Promise<Post | null> => {
  try {
    const posts = await getPostsForSite(domain);
    const post = posts.find(p => p.slug === slug);
    return post || null;
  } catch (error) {
    console.error('Ошибка в getPostBySlug:', error);
    return null;
  }
};

// Функция для получения URL ассета
export const getAssetUrl = (assetId: string | null) => {
  if (!assetId) return null;
  const baseUrl = DIRECTUS_URL.replace(/\/$/, '');
  return `${baseUrl}/assets/${assetId}`;
};